import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  MessageSquare, 
  Users, 
  Search, 
  Plus, 
  Phone,
  Video,
  Paperclip,
  Smile,
  Send,
  Bot,
  Zap,
  Calendar,
  FileText,
  Activity,
  GripVertical
} from 'lucide-react';
import { Button } from '../../ui/button.js';
import AgentCommandPalette from './AgentCommandPalette.js';
import { 
  CommunicationHubProps, 
  Channel, 
  Message, 
  User 
} from './types.js';
import { 
  mockChannels, 
  mockUsers, 
  getMessagesForChannel 
} from './mockData.js';

export const CommunicationHub: React.FC<CommunicationHubProps> = ({
  className = '',
  initialChannelId,
  onChannelChange,
  onMessageSent
}) => {
  const [channels] = useState<Channel[]>(mockChannels);
  const [activeChannelId] = useState<string>(
    initialChannelId || channels[0]?.id || ''
  );
  const [messages, setMessages] = useState<Message[]>([]);
  const [users] = useState<User[]>(mockUsers);
  const [inputValue, setInputValue] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [typingUsers] = useState<string[]>([]);
  const [onlineUsers] = useState<User[]>(mockUsers.filter(u => u.status === 'online'));
  
  const [leftPanelWidth, setLeftPanelWidth] = useState(320);
  const [middlePanelWidth, setMiddlePanelWidth] = useState(400);
  const [isResizing, setIsResizing] = useState<'left' | 'middle' | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (activeChannelId) {
      const channelMessages = getMessagesForChannel(activeChannelId);
      setMessages(channelMessages);
      if (onChannelChange) {
        onChannelChange(activeChannelId);
      }
    }
  }, [activeChannelId, onChannelChange]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleMouseDown = useCallback((panel: 'left' | 'middle') => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(panel);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const mouseX = e.clientX - containerRect.left;

    if (isResizing === 'left') {
      const newWidth = Math.max(240, Math.min(mouseX, containerWidth * 0.4));
      setLeftPanelWidth(newWidth);
    } else if (isResizing === 'middle') {
      const newWidth = Math.max(280, Math.min(mouseX - leftPanelWidth, containerWidth * 0.5));
      setMiddlePanelWidth(newWidth);
    }
  }, [isResizing, leftPanelWidth]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);


  const handleSendMessage = async () => {
    if (!inputValue.trim() && attachments.length === 0) return;

    const newMessage: Message = {
      id: `msg-${Date.now()}`,
      channelId: activeChannelId,
      content: inputValue,
      sender: users[0], // Current user
      timestamp: new Date(),
      type: 'text',
      attachments: attachments.length > 0 ? attachments.map(file => ({
        id: `attach-${Date.now()}`,
        name: file.name,
        type: file.type,
        url: URL.createObjectURL(file),
        size: file.size
      })) : undefined
    };

    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    setAttachments([]);
    setShowCommandPalette(false);

    if (onMessageSent) {
      onMessageSent(newMessage);
    }

    if (inputValue.includes('@ProjectBot') || inputValue.includes('/')) {
      setTimeout(() => {
        const agentResponse: Message = {
          id: `msg-${Date.now() + 1}`,
          channelId: activeChannelId,
          content: generateAgentResponse(inputValue),
          sender: users.find(u => u.isAgent) || users[4],
          timestamp: new Date(),
          type: 'agent',
          metadata: {
            agentAction: true,
            confidence: 0.95
          }
        };
        setMessages(prev => [...prev, agentResponse]);
      }, 1500);
    }
  };

  const generateAgentResponse = (input: string): string => {
    if (input.includes('/summarize')) {
      return "I've generated a summary of the recent conversation. The team discussed project progress, identified resource constraints, and agreed on next steps for the database migration.";
    }
    if (input.includes('/schedule')) {
      return "I've scheduled a meeting for tomorrow at 2 PM. Calendar invites have been sent to all participants.";
    }
    if (input.includes('@ProjectBot')) {
      return "I'm here to help! You can use commands like /summarize, /schedule, /assign, or ask me about project status, deadlines, or team availability.";
    }
    return "I understand your request. Let me process that and get back to you with the relevant information.";
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!showCommandPalette) {
        handleSendMessage();
      }
    }
    
    if (e.key === 'Escape') {
      setShowCommandPalette(false);
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
    
    if (value.startsWith('/') && value.length > 0) {
      setShowCommandPalette(true);
    } else {
      setShowCommandPalette(false);
    }
  };

  const handleExecuteCommand = (command: any, parameters?: string[]) => {
    console.log('Executing command:', command, parameters);
    
    const commandMessage: Message = {
      id: `msg-${Date.now()}`,
      channelId: activeChannelId,
      content: `Executing command: ${command.command}`,
      sender: users[0], // Current user
      timestamp: new Date(),
      type: 'text'
    };
    
    setMessages(prev => [...prev, commandMessage]);
    
    setTimeout(() => {
      const agentUser: User = {
        id: command.agent.toLowerCase(),
        name: command.agent,
        avatar: '/api/placeholder/32/32',
        status: 'online',
        role: 'AI Agent'
      };
      
      const agentResponse: Message = {
        id: `msg-${Date.now() + 1}`,
        channelId: activeChannelId,
        content: generateAgentResponse(command.command),
        sender: agentUser,
        timestamp: new Date(),
        type: 'text'
      };
      setMessages(prev => [...prev, agentResponse]);
    }, 1000);
  };




  return (
    <div ref={containerRef} className={`h-full flex bg-gradient-to-br from-white via-gray-50 to-white rounded-2xl overflow-hidden border border-gray-200 ${className}`}>
      {/* Left Navigation Panel - Tyler Style */}
      <div 
        className="flex flex-col border-r border-gray-200 bg-white relative"
        style={{ width: leftPanelWidth, minWidth: 240 }}
      >
        {/* Comms Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent">Communication Hub</h2>
          </div>
        </div>


        {/* Navigation Menu */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-2">
            <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg hover:bg-gray-50 text-left">
              <div className="w-5 h-5 text-gray-600">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/>
                </svg>
              </div>
              <span className="text-base font-medium text-gray-700">Overview</span>
            </button>

            <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg hover:bg-gray-50 text-left">
              <div className="w-5 h-5 text-gray-600">
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z"/>
                </svg>
              </div>
              <span className="text-base font-medium text-gray-700">All Projects</span>
              <svg className="w-4 h-4 text-gray-500 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
              </svg>
            </button>

            <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg hover:bg-gray-50 text-left">
              <div className="w-5 h-5 text-gray-600">
                <Activity className="w-5 h-5" />
              </div>
              <span className="text-base font-medium text-gray-700">Performance</span>
            </button>

            <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg bg-gradient-to-r from-[#4267A1]/10 to-[#5A7BC4]/10 border border-[#4267A1]/30 text-left">
              <div className="w-5 h-5 text-[#4267A1]">
                <MessageSquare className="w-5 h-5" />
              </div>
              <span className="text-base font-medium text-[#4267A1]">Messages</span>
            </button>

            <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg hover:bg-gray-50 text-left">
              <div className="w-5 h-5 text-gray-600">
                <FileText className="w-5 h-5" />
              </div>
              <span className="text-base font-medium text-gray-700">Documents</span>
              <svg className="w-4 h-4 text-gray-500 ml-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Help Desk Link */}
        <div className="p-6 border-t border-gray-200">
          <button className="text-gray-600 text-base hover:text-[#4267A1] transition-colors">
            Help desk
          </button>
        </div>

        {/* Resize handle for left panel */}
        <div
          className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-gray-300 transition-colors group"
          onMouseDown={handleMouseDown('left')}
        >
          <div className="absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-1/2">
            <GripVertical className="w-3 h-3 text-gray-400 group-hover:text-gray-600 transition-colors" />
          </div>
        </div>
      </div>

      {/* Middle Workspace Panel */}
      <div 
        className="flex flex-col border-r border-gray-200 bg-white relative"
        style={{ width: middlePanelWidth, minWidth: 280 }}
      >

        {/* Add New Messages Button */}
        <div className="p-6">
          <button className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] hover:shadow-lg hover:shadow-[#4267A1]/25 rounded-lg text-white text-base font-medium transition-all duration-200">
            <Plus className="w-5 h-5" />
            <span>Add new messages</span>
          </button>
          <button className="mt-3 p-3 text-gray-600 hover:text-[#4267A1]">
            <Search className="w-5 h-5" />
          </button>
        </div>

        {/* Team Channels */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Team channels</h4>
            <button className="p-2 text-gray-600 hover:text-[#4267A1]">
              <Plus size={16} />
            </button>
          </div>
          <div className="space-y-2">
            <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg bg-gradient-to-r from-[#4267A1]/10 to-[#5A7BC4]/10 border border-[#4267A1]/30 text-left">
              <div className="w-7 h-7 rounded bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] flex items-center justify-center">
                <span className="text-white font-bold text-sm">P</span>
              </div>
              <span className="text-base font-medium text-gray-900">Product Design</span>
            </button>
            <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg hover:bg-gray-50 text-left">
              <div className="w-7 h-7 rounded bg-gray-300 flex items-center justify-center">
                <span className="text-gray-600 font-bold text-sm">D</span>
              </div>
              <span className="text-base font-medium text-gray-700">Development</span>
            </button>
            <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg hover:bg-gray-50 text-left">
              <div className="w-7 h-7 rounded bg-gray-300 flex items-center justify-center">
                <span className="text-gray-600 font-bold text-sm">M</span>
              </div>
              <span className="text-base font-medium text-gray-700">Marketing</span>
            </button>
          </div>
        </div>

        {/* Direct Messages */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Direct messages</h4>
              <button className="p-2 text-gray-600 hover:text-[#4267A1]">
                <Plus size={16} />
              </button>
            </div>
            
            <div className="space-y-2">
              {/* Active conversation */}
              <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg bg-gradient-to-r from-[#4267A1]/10 to-[#5A7BC4]/10 border border-[#4267A1]/30 text-left">
                <div className="relative">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] flex items-center justify-center">
                    <span className="text-white font-medium text-sm">AM</span>
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-white rounded-full border-2 border-gray-200"></div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-base font-medium text-gray-900">Alex Moss</p>
                    <span className="text-sm text-gray-600">11:52</span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">Hi, welcome to UX Bantu!</p>
                </div>
              </button>

              <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg hover:bg-gray-50 text-left">
                <div className="relative">
                  <div className="w-9 h-9 rounded-full bg-[#8A9150] flex items-center justify-center">
                    <span className="text-white font-medium text-sm">AS</span>
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-orange-500 rounded-full border-2 border-white flex items-center justify-center">
                    <span className="text-white text-xs font-bold">12</span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-medium text-gray-700">Agnes Sherman</p>
                  <p className="text-sm text-gray-600 truncate">Online</p>
                </div>
              </button>

              <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg hover:bg-gray-50 text-left">
                <div className="w-9 h-9 rounded-full bg-gray-400 flex items-center justify-center">
                  <span className="text-white font-medium text-sm">RB</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-medium text-gray-700">Rattie Bush</p>
                  <p className="text-sm text-gray-600 truncate">Hi, I'm happy to join</p>
                </div>
              </button>

              <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg hover:bg-gray-50 text-left">
                <div className="w-9 h-9 rounded-full bg-gray-400 flex items-center justify-center">
                  <span className="text-white font-medium text-sm">VA</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-medium text-gray-700">Virgie Arnold</p>
                  <p className="text-sm text-gray-600 truncate">Away</p>
                </div>
              </button>

              <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg hover:bg-gray-50 text-left">
                <div className="w-9 h-9 rounded-full bg-gray-400 flex items-center justify-center">
                  <span className="text-white font-medium text-sm">MR</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-medium text-gray-700">Mittie Rodriquez</p>
                  <p className="text-sm text-gray-600 truncate">Hello guys 👋</p>
                </div>
              </button>

              <button className="flex items-center space-x-3 w-full px-4 py-3 rounded-lg hover:bg-gray-50 text-left">
                <div className="w-9 h-9 rounded-full bg-gray-400 flex items-center justify-center">
                  <span className="text-white font-medium text-sm">DW</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-base font-medium text-gray-700">Derrick Webster</p>
                  <p className="text-xs text-gray-600 truncate">Offline</p>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Resize handle for middle panel */}
        <div
          className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-gray-300 transition-colors group"
          onMouseDown={handleMouseDown('middle')}
        >
          <div className="absolute top-1/2 right-0 transform -translate-y-1/2 translate-x-1/2">
            <GripVertical className="w-3 h-3 text-gray-400 group-hover:text-gray-600 transition-colors" />
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 min-w-0 flex flex-col bg-white h-full">
        {/* Chat Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-gray-600" />
              <span className="text-sm text-gray-600">12</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
              </svg>
              <span className="text-sm text-gray-600">2</span>
            </div>
            <h1 className="text-lg font-semibold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent">Product Design</h1>
          </div>

          <div className="flex items-center space-x-2">
            <button className="flex items-center space-x-2 px-3 py-1.5 bg-[#4267A1]/10 hover:bg-[#4267A1]/20 rounded-lg text-sm text-[#4267A1] transition-colors">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
              </svg>
              <span>Create room</span>
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0 h-full">
          {/* Message from Alex Moss */}
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-medium text-sm">AM</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h4 className="font-medium text-gray-900 text-sm">Alex Moss</h4>
                <span className="text-xs text-gray-500">Today 11:52</span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Hi, welcome to UX Bantu!
              </p>
              <p className="text-gray-700 text-sm leading-relaxed mt-2">
                One of the best design community in town
              </p>
              <span className="text-xs text-gray-500 mt-1 block">Today 11:52</span>
            </div>
          </div>

          {/* Message from Rattie Bush */}
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#8A9150] to-[#9BA55F] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-medium text-sm">RB</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h4 className="font-medium text-gray-900 text-sm">Rattie Bush</h4>
                <span className="text-xs text-gray-500">Today 11:57</span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Hi, I'm happy to join
              </p>
            </div>
          </div>

          {/* Message from Mittie Rodriquez */}
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-medium text-sm">MR</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h4 className="font-medium text-gray-900 text-sm">Mittie Rodriquez</h4>
                <span className="text-xs text-gray-500">Today 12:00</span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Hello guys 👋
              </p>
            </div>
          </div>

          {/* Current user messages */}
          <div className="flex justify-end">
            <div className="max-w-xs">
              <div className="bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] rounded-lg px-3 py-2 mb-1">
                <p className="text-white text-sm">Hi everyone, glad to join to this team</p>
              </div>
              <div className="bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] rounded-lg px-3 py-2">
                <p className="text-white text-sm">I think I found a bug!</p>
              </div>
              <span className="text-xs text-gray-500 mt-1 block text-right">Today 01:05</span>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0 ml-3">
              <span className="text-white font-medium text-sm">YU</span>
            </div>
          </div>

          {/* Message from Alex Moss with quote */}
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-medium text-sm">AM</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h4 className="font-medium text-gray-900 text-sm">Alex Moss</h4>
                <span className="text-xs text-gray-500">Today 11:52</span>
              </div>
              <div className="bg-gray-100 border-l-2 border-[#4267A1] pl-3 py-2 mb-2 text-xs text-gray-600">
                I think I found a bug!
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                Sure? could you show me a screenshot about what happens?
              </p>
            </div>
          </div>

          {/* Current user response */}
          <div className="flex justify-end">
            <div className="max-w-xs">
              <div className="bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] rounded-lg px-3 py-2">
                <p className="text-white text-sm">wait...taking a screenshot right now</p>
              </div>
              <span className="text-xs text-gray-500 mt-1 block text-right">Today 01:05</span>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-400 flex items-center justify-center flex-shrink-0 ml-3">
              <span className="text-white font-medium text-sm">YU</span>
            </div>
          </div>

          {/* Typing Indicators */}
          {typingUsers.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center space-x-3 p-3"
            >
              <div className="w-10 h-10 rounded-full bg-[#4267A1]/10 flex items-center justify-center">
                <Activity size={16} className="text-gray-600" />
              </div>
              <div className="bg-gray-100 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-[#4267A1] rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-[#4267A1] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-[#4267A1] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-xs text-gray-600">
                    {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - Fixed at bottom */}
        <div className="mt-auto border-t border-gray-200 bg-white">
          {/* Attachments Preview */}
          {attachments.length > 0 && (
            <div className="p-3 pb-0 flex flex-wrap gap-2">
              {attachments.map((file, index) => (
                <div key={index} className="flex items-center space-x-2 bg-gray-100 rounded-lg px-3 py-2">
                  <Paperclip size={14} className="text-gray-600" />
                  <span className="text-xs text-gray-700">{file.name}</span>
                  <button
                    onClick={() => removeAttachment(index)}
                    className="text-gray-600 hover:text-red-500"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Agentic Quick Actions */}
          <div className="px-4 pt-3 pb-2 flex items-center space-x-2">
            <Button variant="ghost" size="sm" className="text-gray-600 hover:text-[#4267A1] text-xs">
              <Bot size={14} className="mr-1" />
              @ProjectBot
            </Button>
            <Button variant="ghost" size="sm" className="text-gray-600 hover:text-[#4267A1] text-xs">
              <Zap size={14} className="mr-1" />
              /summarize
            </Button>
            <Button variant="ghost" size="sm" className="text-gray-600 hover:text-[#4267A1] text-xs">
              <Calendar size={14} className="mr-1" />
              /schedule
            </Button>
            <Button variant="ghost" size="sm" className="text-gray-600 hover:text-[#4267A1] text-xs">
              <FileText size={14} className="mr-1" />
              /assign
            </Button>
          </div>

          {/* Message Input */}
          <div className="flex items-center space-x-3 px-4 py-3">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Send your message..."
                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-4 py-2.5 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#4267A1] focus:border-[#4267A1] text-sm"
              />
            </div>

            <button className="p-2 text-gray-600 hover:text-[#4267A1] transition-colors">
              <Plus size={18} />
            </button>

            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() && attachments.length === 0}
              className="bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] hover:shadow-lg hover:shadow-[#4267A1]/25 text-white p-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      </div>


      {/* Agent Command Palette */}
      <AgentCommandPalette
        isVisible={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onExecuteCommand={handleExecuteCommand}
        inputValue={inputValue}
        onInputChange={handleInputChange}
      />
    </div>
  );
};

export default CommunicationHub;
